import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, ArrowRight } from 'lucide-react';

const Section = ({ title, children }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-10"
    >
        <h2 className="font-serif text-xl text-white mb-4 pb-3" style={{ borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
            {title}
        </h2>
        <div className="text-gray-400 text-sm leading-relaxed space-y-3">
            {children}
        </div>
    </motion.div>
);

const PrivacyPage = () => {
    return (
        <div className="font-sans bg-[#0e0e0e] min-h-screen text-white">

            {/* ── Nav bar ── */}
            <div
                className="fixed top-0 left-0 w-full z-50"
                style={{ background: 'rgba(14,14,14,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(212,175,55,0.12)' }}
            >
                {/* Desktop */}
                <div className="hidden md:flex items-center justify-between px-8 py-4">
                    <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-xs uppercase tracking-widest">
                        <ArrowLeft size={15} /> Home
                    </Link>
                    <Link to="/" className="flex items-center gap-2 leading-none">
                        <span className="font-serif text-base text-white tracking-widest">ROYAL</span>
                        <img src="/logo.png" alt="Royal Standard Logo" style={{ height: '28px', width: 'auto', objectFit: 'contain' }} />
                        <span className="font-serif text-base tracking-widest" style={{ color: '#D4AF37' }}>STANDARD</span>
                    </Link>
                    <Link to="/contact" className="text-xs uppercase tracking-widest px-5 py-2.5 font-semibold text-black hover:opacity-90" style={{ background: '#D4AF37' }}>
                        Get a Quote
                    </Link>
                </div>
                {/* Mobile */}
                <div className="md:hidden">
                    <div className="flex items-center justify-between px-4 py-3">
                        <Link to="/" className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors text-xs uppercase tracking-wider">
                            <ArrowLeft size={14} />
                        </Link>
                        <Link to="/" className="flex items-center gap-1.5 leading-none">
                            <span className="font-serif text-sm text-white tracking-widest">ROYAL</span>
                            <img src="/logo.png" alt="Royal Standard Logo" style={{ height: '22px', width: 'auto', objectFit: 'contain' }} />
                            <span className="font-serif text-sm tracking-widest" style={{ color: '#D4AF37' }}>STANDARD</span>
                        </Link>
                        <div className="w-6" />
                    </div>
                    <Link to="/contact" className="flex items-center justify-center gap-2 w-full py-2.5 text-black text-xs uppercase tracking-widest font-semibold" style={{ background: '#D4AF37' }}>
                        Get a Free Quote <ArrowRight size={12} />
                    </Link>
                </div>
            </div>

            {/* ── Hero ── */}
            <section className="pt-32 pb-16 px-5 md:px-6 text-center" style={{ borderBottom: '1px solid rgba(212,175,55,0.1)' }}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.25)' }}
                >
                    <Shield size={26} style={{ color: '#D4AF37' }} />
                </motion.div>
                <motion.p
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="uppercase tracking-[0.3em] text-xs font-semibold mb-4"
                    style={{ color: '#D4AF37' }}
                >
                    Legal
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                    className="font-serif text-4xl md:text-6xl text-white mb-5 leading-tight"
                >
                    Privacy <em className="not-italic" style={{ color: '#D4AF37' }}>Policy</em>
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                    className="text-gray-500 text-sm max-w-xl mx-auto"
                >
                    Last updated: February 2025 &nbsp;·&nbsp; Royal Standard LLC, Dubai, UAE
                </motion.p>
            </section>

            {/* ── Content ── */}
            <div className="max-w-3xl mx-auto px-5 md:px-6 py-16">

                <Section title="1. Introduction">
                    <p>
                        Royal Standard ("we," "us," or "our") operates the website <strong className="text-white">royalstandarduae.com</strong>.
                        This Privacy Policy explains how we collect, use, and protect information about you when you visit our website or contact us for services.
                    </p>
                    <p>
                        By using our website, you agree to the collection and use of information in accordance with this policy.
                    </p>
                </Section>

                <Section title="2. Information We Collect">
                    <p>We may collect the following types of information:</p>
                    <ul className="list-disc list-inside space-y-1 pl-2">
                        <li><strong className="text-white">Contact information</strong> — name, phone number, email address, provided voluntarily when you contact us.</li>
                        <li><strong className="text-white">Project details</strong> — descriptions of your property or requirements shared via WhatsApp or our contact form.</li>
                        <li><strong className="text-white">Usage data</strong> — pages visited, time spent on site, browser type, and device type (collected automatically via standard web analytics).</li>
                    </ul>
                    <p>We do not collect payment details directly — all payments are handled through secure third-party processors.</p>
                </Section>

                <Section title="3. How We Use Your Information">
                    <p>We use collected information to:</p>
                    <ul className="list-disc list-inside space-y-1 pl-2">
                        <li>Respond to your enquiries and provide quotations</li>
                        <li>Schedule and manage service appointments</li>
                        <li>Improve our website and services</li>
                        <li>Send relevant updates (only where you have consented)</li>
                    </ul>
                    <p>We will never sell your personal information to third parties.</p>
                </Section>

                <Section title="4. WhatsApp Communications">
                    <p>
                        When you contact us via WhatsApp, your messages are handled through WhatsApp's platform (Meta Platforms, Inc.).
                        Please review WhatsApp's own Privacy Policy for details on how they process your data.
                        We retain WhatsApp conversation history for service and follow-up purposes only.
                    </p>
                </Section>

                <Section title="5. Cookies">
                    <p>
                        Our website may use cookies to enhance your browsing experience and collect anonymous usage statistics.
                        You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
                    </p>
                </Section>

                <Section title="6. Data Security">
                    <p>
                        We take reasonable precautions to protect your information against unauthorised access, alteration, disclosure or destruction.
                        However, no method of transmission over the internet is 100% secure and we cannot guarantee absolute security.
                    </p>
                </Section>

                <Section title="7. Third-Party Links">
                    <p>
                        Our website may contain links to third-party websites (e.g., Google Maps, WhatsApp). We are not responsible for
                        the privacy practices or content of those sites and encourage you to review their privacy policies.
                    </p>
                </Section>

                <Section title="8. Your Rights">
                    <p>You have the right to:</p>
                    <ul className="list-disc list-inside space-y-1 pl-2">
                        <li>Request access to personal data we hold about you</li>
                        <li>Request correction or deletion of your data</li>
                        <li>Opt out of marketing communications at any time</li>
                    </ul>
                    <p>To exercise any of these rights, please contact us at <a href="mailto:info@royalstandarduae.com" className="text-yellow-400 hover:underline">info@royalstandarduae.com</a>.</p>
                </Section>

                <Section title="9. Changes to This Policy">
                    <p>
                        We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated date.
                        Continued use of our website after changes constitutes your acceptance of the updated policy.
                    </p>
                </Section>

                <Section title="10. Contact Us">
                    <p>If you have any questions about this Privacy Policy, please contact us:</p>
                    <ul className="list-disc list-inside space-y-1 pl-2">
                        <li>Email: <a href="mailto:info@royalstandarduae.com" className="text-yellow-400 hover:underline">info@royalstandarduae.com</a></li>
                        <li>Phone: <a href="tel:+971581735200" className="text-yellow-400 hover:underline">+971 58 173 5200</a></li>
                        <li>Location: Dubai, United Arab Emirates</li>
                    </ul>
                </Section>

                {/* CTA */}
                <div className="mt-12 text-center">
                    <Link
                        to="/terms"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 hover:text-yellow-400 transition-colors"
                    >
                        Read our Terms & Conditions <ArrowRight size={13} />
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPage;
